import { ProjectColoredLink } from "@selector/shared/components";
import { type ReactElement, useState } from "react";
import {
  ActionButton,
  ActionButtonIconPlacement,
  ActionButtonIntent,
  ActionButtonSize,
  Center,
  Collapse,
  TimeAgo,
  VStack,
} from "widgets";

import type { Project, ProjectActivity as ProjectActivityGql, User } from "../../../graphql.js";
import { ProjectActivity } from "../../projects/ProjectActivity.js";
import styles from "./RelatedProjectPanel.module.css";

export interface IRelatedProjectComponentProps {
  project: Project;
  activities: ProjectActivityGql[];
  currentUser: User;
  collapsed?: boolean;
}

const INITIALLY_SHOWN = 1;

// defines how many activities are always shown

/**
 * A panel for a single project related to the main object of the RelatedProjectCard.
 * The list of activities for this project is collapsible after the first N activities.
 *
 * created on 11/11/16
 */
export function RelatedProjectPanel(props: IRelatedProjectComponentProps): ReactElement {
  const [collapsed, setCollapsed] = useState(!!props.collapsed);

  const togglePanel = () => {
    setCollapsed(!collapsed);
  };

  const buildActivities = (activities: ProjectActivityGql[]): ReactElement[] => {
    const projectId: number = props.project.id;
    const currentUser: User = props.currentUser;

    return activities.map((activity) => {
      return (
        <ProjectActivity
          key={`rel-prj-${projectId}-act-${activity.id}`}
          activity={activity}
          currentUser={currentUser}
          users={props.project.projectMembers ? (props.project.projectMembers.map((x) => x.user) as User[]) : []}
        />
      );
    });
  };

  const buildCollapsibleActivities = (activities: ProjectActivityGql[]): ReactElement => {
    return (
      <Collapse isExpanded={!collapsed}>
        <VStack domProps={{ role: "list" }} className={styles.list} spacing={10}>
          {buildActivities(activities)}
        </VStack>
      </Collapse>
    );
  };

  const buildToggle = (): ReactElement => {
    return (
      <Center>
        <ActionButton
          icon={collapsed ? "keyboard_double_arrow_down" : "keyboard_double_arrow_up"}
          label={collapsed ? `Show All ${props.activities.length} Activities` : "Show Fewer Activities"}
          onPress={togglePanel}
          tooltip={`View all activities related to this genome in ${props.project.name}`}
          size={ActionButtonSize.Small}
          iconPlacement={ActionButtonIconPlacement.Right}
          intent={ActionButtonIntent.Outlined}
          style={{ marginTop: 10 }}
        />
      </Center>
    );
  };

  const project: Project = props.project;

  let visibleActivities: ReactElement[] | null = null;
  let toggleDisplay: ReactElement | null = null;
  let collapsibleActivities: ReactElement | null = null;

  if (props.activities) {
    visibleActivities = buildActivities(props.activities.slice(0, INITIALLY_SHOWN));

    if (props.activities.length > INITIALLY_SHOWN) {
      collapsibleActivities = buildCollapsibleActivities(props.activities.slice(INITIALLY_SHOWN));
      toggleDisplay = buildToggle();
    }
  }

  return (
    <div>
      <div style={{ marginBottom: 10 }}>
        <span className="highlight-title">
          <ProjectColoredLink project={project} />
        </span>
        <br />
        {project.lastModified ? (
          <span className="byline">
            last activity <TimeAgo dateTimeISO={project.lastModified} />
          </span>
        ) : null}
      </div>
      <div style={{ marginBottom: 10 }}>{project.description}</div>
      <ul className={styles.list} style={{ marginBottom: collapsed ? 0 : 10 }}>
        {visibleActivities}
      </ul>
      {collapsibleActivities}
      {toggleDisplay}
    </div>
  );
}

export default RelatedProjectPanel;
